import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Loader2,
} from 'lucide-react';
import { getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost } from '../../services/api/sdk.gen';
import { fetchRogerEmrFormSchemaVersions } from './fetchRogerEmrFormSchemaVersions';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import type { FormTree } from './formSchemaTypes';
import {
  layoutGenerationKey,
  templatesBySectionForPin,
  orderedFormTreeSectionIds,
  pinFromFormSchemaVersion,
  sectionValidationStatusForLayout,
} from './formSchemaUtils';
import { SelectableNestedTable } from './SelectableNestedTable';
import type { SelectableNestedTableRowModel } from './bespokeTemplatesImport';
import {
  buildOrderedValidationGroups,
  buildValidationRows,
  buildValidationSchemaJobs,
  filterTemplatesForValidationJob,
  rollupValidationStatus,
  schemaVersionNamesInTemplates,
  versionLoadFailureGroupKey,
  type SchemaVersionValidation,
  type ValidationStatus,
} from './validationResults';
import { sortFormTypeCatalogByPrecedence } from './formPrecedence';
import {
  buildLiveConsistencyIndexes,
  rollupSectionConsistency,
  sectionConsistencyForTemplate,
  type SectionConsistency,
} from './sectionConsistency';
import { SectionConsistencyBadge } from './SectionConsistencyBadge';
import { collectQuestionTreeQids } from '../../components/oasis/walkQuestionTree';

const VERSION_FETCH_CONCURRENCY = 6;
const SCHEMA_FETCH_CONCURRENCY = 4;

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index]);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    worker()
  );
  await Promise.all(workers);
  return results;
}

function isAbortError(err: unknown): boolean {
  if (err instanceof Error && err.name === 'CanceledError') return true;
  return (
    typeof err === 'object' &&
    err != null &&
    'code' in err &&
    (err as { code?: string }).code === 'ERR_CANCELED'
  );
}

interface ValidationResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: BespokeTemplateResponse[];
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  /** Preloaded schema versions (e.g. current form type) to skip redundant fetches. */
  seedVersionsByFormType?: Map<RogerDocFormType, FormSchemaVersion[]>;
}

/** Coverage icon for a section row. Pure in `status`. */
function renderCoverage(status: ValidationStatus) {
  if (status === 'loading') {
    return (
      <Loader2
        className='h-4 w-4 animate-spin text-muted-foreground'
        aria-label='Checking'
      />
    );
  }
  if (status === 'pass') {
    return (
      <CheckCircle2 className='h-4 w-4 text-emerald-600' aria-label='Pass' />
    );
  }
  if (status === 'missing') {
    return (
      <CircleDashed
        className='h-4 w-4 text-muted-foreground'
        aria-label='No layout'
      />
    );
  }
  return (
    <AlertTriangle
      className='h-4 w-4 text-amber-600'
      aria-label={status === 'error' ? 'Error' : 'Fail'}
    />
  );
}

export function ValidationResultsDialog({
  open,
  onOpenChange,
  templates,
  formTypeCatalog,
  seedVersionsByFormType,
}: ValidationResultsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<SchemaVersionValidation[]>([]);
  const [statusByNodeId, setStatusByNodeId] = useState<
    Map<string, ValidationStatus>
  >(() => new Map());
  const [consistencyByNodeId, setConsistencyByNodeId] = useState<
    Map<string, SectionConsistency>
  >(() => new Map());
  const [schemaVersionErrors, setSchemaVersionErrors] = useState<
    Map<string, string>
  >(() => new Map());
  const loadGenerationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const versionsCacheRef = useRef(
    new Map<RogerDocFormType, FormSchemaVersion[]>()
  );

  const templateSchemaNamesKey = useMemo(
    () => schemaVersionNamesInTemplates(templates).sort().join('\0'),
    [templates]
  );

  /**
   * Built once per template list rather than per section: the index is the
   * whole catalog, so building it inside the per-section loop would redo
   * the same walk for every row.
   */
  const consistencyIndexes = useMemo(
    () => buildLiveConsistencyIndexes(templates),
    [templates]
  );

  const resetState = useCallback(() => {
    setGroups([]);
    setStatusByNodeId(new Map());
    setConsistencyByNodeId(new Map());
    setSchemaVersionErrors(new Map());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      loadGenerationRef.current += 1;
      versionsCacheRef.current.clear();
      resetState();
      return;
    }
    if (formTypeCatalog.length === 0 || templateSchemaNamesKey.length === 0) {
      resetState();
      return;
    }

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    if (seedVersionsByFormType) {
      for (const [ft, vers] of seedVersionsByFormType) {
        versionsCacheRef.current.set(ft, vers);
      }
    }

    const generation = ++loadGenerationRef.current;
    const stale = () =>
      signal.aborted || loadGenerationRef.current !== generation;

    void (async () => {
      setLoading(true);
      setGroups([]);
      setStatusByNodeId(new Map());
      setConsistencyByNodeId(new Map());
      setSchemaVersionErrors(new Map());

      try {
        const sortedCatalog = sortFormTypeCatalogByPrecedence(formTypeCatalog);
        const versionsMap = new Map<RogerDocFormType, FormSchemaVersion[]>();
        const versionLoadFailedFormTypes = new Set<RogerDocFormType>();
        await mapWithConcurrency(
          sortedCatalog,
          VERSION_FETCH_CONCURRENCY,
          async row => {
            if (stale()) return;
            const formType = row.form_type as RogerDocFormType;
            const cached = versionsCacheRef.current.get(formType);
            if (cached) {
              versionsMap.set(formType, cached);
              return;
            }
            try {
              const versions = await fetchRogerEmrFormSchemaVersions(
                row.canonical_visit_type_id,
                { signal }
              );
              versionsCacheRef.current.set(formType, versions);
              versionsMap.set(formType, versions);
            } catch (err) {
              if (isAbortError(err) || stale()) return;
              console.error(
                `Failed to fetch schema versions for ${formType}:`,
                err
              );
              versionLoadFailedFormTypes.add(formType);
              versionsMap.set(formType, []);
            }
          }
        );
        if (stale()) return;

        const validationJobs = buildValidationSchemaJobs({
          templates,
          catalog: formTypeCatalog,
          versionsByFormType: versionsMap,
        });

        const nextGroups = buildOrderedValidationGroups({
          catalog: formTypeCatalog,
          jobs: validationJobs,
          failedFormTypes: versionLoadFailedFormTypes,
        });

        const versionLoadFailureKeys = new Set(
          [...versionLoadFailedFormTypes].map(versionLoadFailureGroupKey)
        );

        const nextStatus = new Map<string, ValidationStatus>();
        const nextSchemaVersionErrors = new Map<string, string>();
        for (const group of nextGroups) {
          if (versionLoadFailureKeys.has(group.schemaVersionKey)) {
            nextStatus.set(group.schemaVersionKey, 'error');
            nextSchemaVersionErrors.set(
              group.schemaVersionKey,
              'Schema version list could not be loaded.'
            );
          } else {
            nextStatus.set(group.schemaVersionKey, 'loading');
          }
        }

        if (stale()) return;
        setGroups(nextGroups);
        setStatusByNodeId(nextStatus);
        setSchemaVersionErrors(nextSchemaVersionErrors);

        const reportGroupError = (groupKey: string, message: string) => {
          if (stale()) return;
          setSchemaVersionErrors(prev => {
            const next = new Map(prev);
            next.set(groupKey, message);
            return next;
          });
        };

        const applyResolvedGroup = (
          groupKey: string,
          sectionResults: {
            sectionId: string;
            label: string;
            status: ValidationStatus;
            consistency?: SectionConsistency;
          }[]
        ) => {
          if (stale()) return;
          setGroups(prev =>
            prev.map(group =>
              group.schemaVersionKey === groupKey
                ? {
                    ...group,
                    sections: sectionResults.map(s => ({
                      sectionId: s.sectionId,
                      label: s.label,
                      pass: s.status === 'pass',
                    })),
                  }
                : group
            )
          );
          setStatusByNodeId(prev => {
            const next = new Map(prev);
            for (const section of sectionResults) {
              next.set(section.sectionId, section.status);
            }
            next.set(groupKey, rollupValidationStatus(sectionResults));
            return next;
          });
          setConsistencyByNodeId(prev => {
            const next = new Map(prev);
            const resolved: SectionConsistency[] = [];
            for (const section of sectionResults) {
              if (!section.consistency) continue;
              next.set(section.sectionId, section.consistency);
              resolved.push(section.consistency);
            }
            next.set(groupKey, rollupSectionConsistency(resolved));
            return next;
          });
        };

        if (validationJobs.length === 0) {
          if (!stale()) setLoading(false);
          return;
        }

        await mapWithConcurrency(
          validationJobs,
          SCHEMA_FETCH_CONCURRENCY,
          async job => {
            if (stale()) return;
            const { version, groupKey, visitType } = job;
            try {
              const res =
                await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
                  query: { visit_type: visitType },
                  body: {
                    schema_version: {
                      name: version.name,
                      version: version.version ?? null,
                    },
                    // Forms Manager authors against the same question set the
                    // MCP sees.
                    omit_inline_goals: true,
                  },
                  signal,
                  throwOnError: true,
                });
              if (stale()) return;

              const payload = res?.data as VersionedQuestionSchema | undefined;
              const tree = (payload?.schema_data ?? null) as FormTree | null;
              if (!tree) {
                throw new Error('Empty schema response');
              }
              if (!version.version) {
                throw new Error(
                  'Schema version is missing a version identifier'
                );
              }

              const sectionIds = orderedFormTreeSectionIds(
                tree,
                payload?.version.section_order
              );
              const labelByCategory: Record<string, string> = {};
              for (const item of payload?.version.section_order ?? []) {
                labelByCategory[item.section_id] = item.display_name;
              }

              const templatesForJob = filterTemplatesForValidationJob({
                job,
                templates,
                versionsByFormType: versionsMap,
                sectionIds,
              });
              const validationPin = pinFromFormSchemaVersion(
                job.formType,
                version
              );
              if (!validationPin) {
                throw new Error(
                  'Schema version is missing a version identifier'
                );
              }
              const layoutBySection = templatesBySectionForPin(
                templatesForJob,
                validationPin
              );

              const sectionResults = sectionIds.map(sectionId => {
                const sectionIdKey = layoutGenerationKey(groupKey, sectionId);
                const questions = tree[sectionId] ?? [];
                const validationStatus = sectionValidationStatusForLayout(
                  layoutBySection.get(sectionId),
                  questions
                );
                return {
                  sectionId: sectionIdKey,
                  label: formatSectionDropdownLabel(sectionId, labelByCategory),
                  status: validationStatus as ValidationStatus,
                  // Independent of coverage: a section can cover every question
                  // and still design them unlike every other form that shares
                  // them. Computed from the same template the coverage
                  // check just read, so the two always describe one layout.
                  // The section's own QIDs come along so a gate this form
                  // cannot express -- a question re-issued under a new id --
                  // is not reported as drift no layout can fix.
                  consistency: sectionConsistencyForTemplate(
                    layoutBySection.get(sectionId),
                    consistencyIndexes,
                    new Set(collectQuestionTreeQids(questions))
                  ),
                };
              });

              applyResolvedGroup(groupKey, sectionResults);
            } catch (err) {
              if (isAbortError(err) || stale()) return;
              console.error(
                `Validation schema fetch failed for ${job.groupLabel}:`,
                err
              );
              const message = 'Could not load section schema for validation.';
              reportGroupError(groupKey, message);
              applyResolvedGroup(groupKey, []);
            }
          }
        );
      } finally {
        if (!stale()) setLoading(false);
      }
    })();

    return () => {
      abortController.abort();
      loadGenerationRef.current += 1;
    };
  }, [
    consistencyIndexes,
    formTypeCatalog,
    open,
    resetState,
    templateSchemaNamesKey,
    templates,
    seedVersionsByFormType,
  ]);

  const rows: SelectableNestedTableRowModel[] = useMemo(
    () => buildValidationRows(groups),
    [groups]
  );

  const groupLabelByKey = useMemo(
    () => new Map(groups.map(g => [g.schemaVersionKey, g.label])),
    [groups]
  );

  const renderAccessory = useCallback(
    (node: SelectableNestedTableRowModel) => {
      const status = statusByNodeId.get(node.id) ?? 'loading';
      const consistency = consistencyByNodeId.get(node.id);
      return (
        <span className='inline-flex items-center gap-2'>
          {renderCoverage(status)}
          {consistency ? <SectionConsistencyBadge state={consistency} /> : null}
        </span>
      );
    },
    [consistencyByNodeId, statusByNodeId]
  );

  const hasTemplatesToValidate = templateSchemaNamesKey.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[min(90vh,52rem)] max-w-3xl flex-col'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>Validation Results</DialogTitle>
          <DialogDescription>
            Coverage validation for bespoke layouts. Sections without a layout
            show as incomplete.
          </DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex-1 space-y-2 overflow-y-auto'>
          {loading && rows.length === 0 ? (
            <div
              className='flex min-h-[12rem] items-center justify-center gap-2 text-sm text-muted-foreground'
              role='status'
              aria-busy='true'
            >
              <Loader2 className='h-5 w-5 animate-spin' aria-hidden />
              <span>Loading validation results…</span>
            </div>
          ) : !hasTemplatesToValidate ? (
            <p className='text-sm text-muted-foreground'>
              No bespoke templates to validate.
            </p>
          ) : rows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              Could not load schema versions for validation. Try closing and
              reopening the dialog.
            </p>
          ) : (
            <>
              {loading ? (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' aria-hidden />
                  <span>Checking layouts…</span>
                </div>
              ) : null}
              {[...schemaVersionErrors.entries()].map(([key, message]) => (
                <p key={key} className='text-xs text-destructive'>
                  {groupLabelByKey.get(key) ?? key}: {message}
                </p>
              ))}
              <SelectableNestedTable
                className='min-h-[12rem] max-h-[min(48vh,24rem)] overflow-y-auto'
                rows={rows}
                renderAccessory={renderAccessory}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
